import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { Play, Pause, Volume2, VolumeX, Maximize } from "lucide-react";

const HlsPlayer = ({ src, poster, testIdPrefix = "hls-player" }) => {
  const videoRef = useRef(null);
  const wrapRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;
    let hls;
    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = src;
      video.addEventListener("loadedmetadata", () => setReady(true), { once: true });
    } else if (Hls.isSupported()) {
      hls = new Hls({ enableWorker: true, lowLatencyMode: true });
      hls.loadSource(src);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => setReady(true));
    }
    return () => { if (hls) hls.destroy(); };
  }, [src]);

  const toggle = () => {
    const v = videoRef.current; if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };
  const toggleMute = () => { const v = videoRef.current; if (!v) return; v.muted = !v.muted; setMuted(v.muted); };
  const fs = () => { const el = wrapRef.current; if (!el) return; if (document.fullscreenElement) document.exitFullscreen(); else el.requestFullscreen?.(); };

  return (
    <div ref={wrapRef} className="relative rounded-xl overflow-hidden bg-black border border-white/5 shadow-2xl group">
      <video data-testid={`${testIdPrefix}-video`} ref={videoRef} poster={poster} muted={muted} playsInline autoPlay className="w-full aspect-video object-contain bg-black" onClick={toggle} />
      <div className="absolute top-3 left-3 flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 bg-red-600 text-white text-[11px] mono font-bold uppercase tracking-widest px-2 py-1 rounded">
          <span className="w-1.5 h-1.5 rounded-full bg-white live-dot" /> Live
        </span>
        {!ready && <span className="text-[11px] mono text-slate-300 bg-black/60 px-2 py-1 rounded">Buffering…</span>}
      </div>
      <div className="absolute inset-x-0 bottom-0 p-3 flex items-center gap-2 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
        <button data-testid={`${testIdPrefix}-play-button`} onClick={toggle} className="w-9 h-9 rounded-md bg-white/10 hover:bg-white/20 grid place-items-center">
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>
        <button data-testid={`${testIdPrefix}-mute-button`} onClick={toggleMute} className="w-9 h-9 rounded-md bg-white/10 hover:bg-white/20 grid place-items-center">
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
        <div className="flex-1" />
        <button data-testid={`${testIdPrefix}-fullscreen-button`} onClick={fs} className="w-9 h-9 rounded-md bg-white/10 hover:bg-white/20 grid place-items-center">
          <Maximize className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default HlsPlayer;
